import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Zap, Calendar, DollarSign, AlertCircle, CheckCircle, RefreshCw, FileText, Baby
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const AutoInvoiceGenerator = ({ onInvoicesCreated }) => {
  const { getAuthHeaders } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceTypes, setInvoiceTypes] = useState({
    tuition: true,
    registration: true
  });
  const [preview, setPreview] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    try {
      const types = [];
      if (invoiceTypes.tuition) types.push('tuition');
      if (invoiceTypes.registration) types.push('registration');
      
      const response = await axios.post(`${API}/billing/generate-invoices`, {
        target_date: targetDate,
        invoice_types: types,
        dry_run: true
      }, getAuthHeaders());
      
      setPreview(response.data);
    } catch (error) {
      toast.error('Failed to preview invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!preview || preview.invoices_count === 0) {
      toast.error('No invoices to generate');
      return;
    }
    
    setGenerating(true);
    try {
      const types = [];
      if (invoiceTypes.tuition) types.push('tuition');
      if (invoiceTypes.registration) types.push('registration');
      
      const response = await axios.post(`${API}/billing/generate-invoices`, {
        target_date: targetDate,
        invoice_types: types,
        dry_run: false
      }, getAuthHeaders());
      
      toast.success(`Successfully generated ${response.data.invoices_created} invoices!`);
      setDialogOpen(false);
      setPreview(null);
      if (onInvoicesCreated) onInvoicesCreated();
    } catch (error) {
      toast.error('Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  };

  const resetDialog = () => {
    setPreview(null);
    setTargetDate(new Date().toISOString().split('T')[0]);
    setInvoiceTypes({ tuition: true, registration: true });
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetDialog(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="auto-invoice-btn">
          <Zap className="w-4 h-4 mr-2" />
          Auto Generate
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Automated Invoice Generation
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Info Banner */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">How it works:</p>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li><strong>Tuition:</strong> Generated on Mondays (weekly) or 1st of month (monthly)</li>
                <li><strong>Registration:</strong> Generated on scheduled billing dates</li>
                <li>Only children with "Auto-Generate Invoices" enabled will be billed</li>
              </ul>
            </div>
          </div>

          {/* Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Target Date</Label>
              <Input
                type="date"
                value={targetDate}
                onChange={(e) => { setTargetDate(e.target.value); setPreview(null); }}
                data-testid="auto-invoice-date"
              />
              <p className="text-xs text-muted-foreground">
                Simulate invoice generation for this date
              </p>
            </div>
            <div className="space-y-3">
              <Label>Invoice Types</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="type-tuition"
                    checked={invoiceTypes.tuition}
                    onCheckedChange={(checked) => { setInvoiceTypes(prev => ({ ...prev, tuition: checked })); setPreview(null); }}
                  />
                  <Label htmlFor="type-tuition" className="cursor-pointer flex items-center gap-1">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    Tuition
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="type-registration"
                    checked={invoiceTypes.registration}
                    onCheckedChange={(checked) => { setInvoiceTypes(prev => ({ ...prev, registration: checked })); setPreview(null); }}
                  />
                  <Label htmlFor="type-registration" className="cursor-pointer flex items-center gap-1">
                    <FileText className="w-4 h-4 text-amber-500" />
                    Registration Fee
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Button */}
          <Button 
            onClick={handlePreview} 
            disabled={loading || (!invoiceTypes.tuition && !invoiceTypes.registration)}
            className="w-full"
            variant="outline"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Loading Preview...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Preview Invoices for {targetDate}
              </>
            )}
          </Button>

          {/* Preview Results */}
          {preview && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-primary">{preview.invoices_count}</p>
                  <p className="text-xs text-muted-foreground">Invoices</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
                  <p className="text-2xl font-bold text-green-600">${preview.total_amount?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-green-600">Total Amount</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-200">
                  <p className="text-2xl font-bold text-blue-600">{preview.target_date}</p>
                  <p className="text-xs text-blue-600">Target Date</p>
                </div>
              </div>

              {/* Invoice List */}
              <ScrollArea className="h-48 border rounded-lg">
                {preview.invoices && preview.invoices.length > 0 ? (
                  <div className="divide-y">
                    {preview.invoices.map((inv, idx) => (
                      <div key={idx} className="p-3 flex items-center justify-between hover:bg-muted/30">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            inv.invoice_type === 'tuition' ? 'bg-green-100' : 'bg-amber-100'
                          }`}>
                            {inv.invoice_type === 'tuition' ? (
                              <DollarSign className="w-4 h-4 text-green-600" />
                            ) : (
                              <FileText className="w-4 h-4 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{inv.child_name}</p>
                            <p className="text-xs text-muted-foreground">{inv.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={inv.invoice_type === 'tuition' ? 'default' : 'secondary'} className="text-xs mb-1">
                            {inv.invoice_type}
                          </Badge>
                          <p className="font-bold text-green-600">${inv.amount.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500 opacity-50" />
                    <p className="font-medium">No invoices due for this date</p>
                    <p className="text-xs mt-1">
                      Tuition: Mondays (weekly) or 1st (monthly)<br/>
                      Registration: On scheduled billing dates
                    </p>
                  </div>
                )}
              </ScrollArea>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetDialog(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerate} 
              disabled={generating || !preview || preview.invoices_count === 0}
              data-testid="generate-invoices-btn"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Generate {preview?.invoices_count || 0} Invoices
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AutoInvoiceGenerator;
